import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import AdminSidebar from '../../components/ui/AdminSidebar';
import FloatingAIChat from '../../components/ui/FloatingAIChat';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { Checkbox } from '../../components/ui/Checkbox';
import Icon from '../../components/AppIcon';

const SystemBackupConfiguration = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState(searchParams?.get('tab') || 'overview');
  const [backups, setBackups] = useState([
    {
      id: 1,
      name: 'Backup Completo - Sistema',
      type: 'full',
      date: '2025-01-14T02:00:00Z',
      size: '2.3 GB',
      status: 'completed',
      integrity: 'verified',
      includes: ['users', 'courses', 'media', 'settings']
    },
    {
      id: 2,
      name: 'Backup Incremental - Dados',
      type: 'incremental',
      date: '2025-01-14T08:30:00Z',
      size: '145 MB',
      status: 'completed',
      integrity: 'verified',
      includes: ['users', 'courses']
    },
    {
      id: 3,
      name: 'Backup de Mídia',
      type: 'media',
      date: '2025-01-13T23:15:00Z',
      size: '5.7 GB',
      status: 'completed',
      integrity: 'verified',
      includes: ['media']
    },
    {
      id: 4,
      name: 'Backup Manual - Emergência',
      type: 'manual',
      date: '2025-01-13T14:22:00Z',
      size: '1.8 GB',
      status: 'completed',
      integrity: 'verified',
      includes: ['users', 'courses', 'settings']
    }
  ]);

  const [backupSettings, setBackupSettings] = useState({
    frequency: 'daily',
    time: '02:00',
    retention: '30',
    storageLocation: 'local',
    encryptionEnabled: true,
    compressionEnabled: true,
    includeMedia: true,
    includeUserData: true,
    includeCourseContent: true,
    includeSystemSettings: true,
    notifications: true,
    cloudProvider: 'aws',
    cloudCredentials: {
      accessKey: '',
      secretKey: '',
      bucketName: '',
      region: 'us-east-1'
    }
  });

  const [backupProgress, setBackupProgress] = useState({
    isRunning: false,
    currentOperation: '',
    progress: 0,
    eta: ''
  });

  const [systemHealth, setSystemHealth] = useState({
    diskSpace: {
      total: '500 GB',
      used: '245 GB',
      available: '255 GB',
      percentage: 49
    },
    lastBackup: '2025-01-14T08:30:00Z',
    backupStatus: 'healthy',
    storageHealth: 'healthy',
    networkStatus: 'connected'
  });

  useEffect(() => {
    const tab = searchParams?.get('tab') || 'overview';
    setActiveTab(tab);
  }, [searchParams]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'overview') {
      searchParams?.delete('tab');
    } else {
      searchParams?.set('tab', tab);
    }
    setSearchParams(searchParams);
  };

  const handleBackupNow = () => {
    setBackupProgress({
      isRunning: true,
      currentOperation: 'Iniciando backup completo...',
      progress: 0,
      eta: '15 minutos estimados'
    });

    // Simulate backup progress
    const interval = setInterval(() => {
      setBackupProgress(prev => {
        if (prev?.progress >= 100) {
          clearInterval(interval);
          return {
            isRunning: false,
            currentOperation: 'Backup concluído com sucesso!',
            progress: 100,
            eta: ''
          };
        }
        return {
          ...prev,
          progress: prev?.progress + Math.random() * 10,
          currentOperation: prev?.progress < 30 ? 'Copiando dados de usuários...' :
                          prev?.progress < 60 ? 'Copiando conteúdo dos cursos...' :
                          prev?.progress < 90 ? 'Comprimindo arquivos...' : 'Finalizando backup...'
        };
      });
    }, 1000);
  };

  const handleRestoreBackup = (backupId) => {
    if (confirm('Tem certeza que deseja restaurar este backup? Esta operação pode levar alguns minutos.')) {
      alert('Funcionalidade de restauração está sendo implementada. Entre em contato com o suporte para restaurações emergenciais.');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (size) => {
    return size;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-success';
      case 'running': return 'text-warning';
      case 'failed': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return 'CheckCircle';
      case 'running': return 'Clock';
      case 'failed': return 'XCircle';
      default: return 'Circle';
    }
  };

  const breadcrumbItems = [
    { label: 'Início', path: '/dashboard-home' },
    { label: 'Sistema', path: '/admin/system' },
    { label: 'Backup', path: '/admin/system/backup', isActive: true }
  ];

  const tabs = [
    { id: 'overview', label: 'Visão Geral', icon: 'LayoutDashboard' },
    { id: 'configuration', label: 'Configuração', icon: 'Settings' },
    { id: 'history', label: 'Histórico', icon: 'History' },
    { id: 'restore', label: 'Restauração', icon: 'RotateCcw' },
    { id: 'monitoring', label: 'Monitoramento', icon: 'Activity' }
  ];

  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* System Health Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card p-6 rounded-lg border border-border shadow-tier-1">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-muted-foreground">Espaço em Disco</h3>
            <Icon name="HardDrive" size={20} className="text-primary" />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Usado</span>
              <span className="font-medium">{systemHealth?.diskSpace?.used}</span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className="bg-primary h-2 rounded-full" 
                style={{ width: `${systemHealth?.diskSpace?.percentage}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Disponível: {systemHealth?.diskSpace?.available}</span>
              <span>{systemHealth?.diskSpace?.percentage}%</span>
            </div>
          </div>
        </div>

        <div className="bg-card p-6 rounded-lg border border-border shadow-tier-1">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-muted-foreground">Último Backup</h3>
            <Icon name="Clock" size={20} className="text-success" />
          </div>
          <p className="text-lg font-semibold text-foreground">
            {formatDate(systemHealth?.lastBackup)}
          </p>
          <p className="text-sm text-success">Concluído com sucesso</p>
        </div>

        <div className="bg-card p-6 rounded-lg border border-border shadow-tier-1">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-muted-foreground">Status do Sistema</h3>
            <Icon name="Shield" size={20} className="text-success" />
          </div>
          <p className="text-lg font-semibold text-success">Saudável</p>
          <p className="text-sm text-muted-foreground">Todos os sistemas operacionais</p>
        </div>

        <div className="bg-card p-6 rounded-lg border border-border shadow-tier-1">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-muted-foreground">Conexão de Rede</h3>
            <Icon name="Wifi" size={20} className="text-success" />
          </div>
          <p className="text-lg font-semibold text-success">Conectado</p>
          <p className="text-sm text-muted-foreground">Upload/Download ativos</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-card p-6 rounded-lg border border-border shadow-tier-1">
        <h3 className="text-lg font-semibold text-foreground mb-4">Ações Rápidas</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Button 
            variant="default" 
            size="lg" 
            className="h-20 flex-col"
            data-testid="btn-backup-now"
            onClick={handleBackupNow}
            disabled={backupProgress?.isRunning}
          >
            <Icon name="Database" size={24} className="mb-2" />
            {backupProgress?.isRunning ? 'Executando Backup...' : 'Backup Agora'}
          </Button>
          
          <Button 
            variant="outline" 
            size="lg" 
            className="h-20 flex-col"
            onClick={() => handleTabChange('configuration')}
          >
            <Icon name="Settings" size={24} className="mb-2" />
            Configurar Backup
          </Button>
          
          <Button 
            variant="outline" 
            size="lg" 
            className="h-20 flex-col"
            onClick={() => handleTabChange('restore')}
          >
            <Icon name="RotateCcw" size={24} className="mb-2" />
            Restaurar Backup
          </Button>
        </div>
      </div>

      {/* Progress Monitor */}
      {backupProgress?.isRunning && (
        <div className="bg-card p-6 rounded-lg border border-border shadow-tier-1">
          <h3 className="text-lg font-semibold text-foreground mb-4">Progresso do Backup</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-muted-foreground">Operação atual</span>
                <span className="font-medium">{Math.round(backupProgress?.progress)}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-3">
                <div 
                  className="bg-primary h-3 rounded-full transition-all duration-500" 
                  style={{ width: `${backupProgress?.progress}%` }}
                />
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-foreground">{backupProgress?.currentOperation}</span>
              <span className="text-sm text-muted-foreground">{backupProgress?.eta}</span>
            </div>
          </div>
        </div>
      )}

      {/* Recent Backups */}
      <div className="bg-card p-6 rounded-lg border border-border shadow-tier-1">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-foreground">Backups Recentes</h3>
          <Button variant="outline" size="sm" onClick={() => handleTabChange('history')}>
            Ver Todos
          </Button>
        </div>
        <div className="space-y-3">
          {backups?.slice(0, 3)?.map((backup) => (
            <div key={backup?.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center space-x-3">
                <Icon name={getStatusIcon(backup?.status)} size={16} className={getStatusColor(backup?.status)} />
                <div>
                  <p className="font-medium text-foreground">{backup?.name}</p>
                  <p className="text-sm text-muted-foreground">{formatDate(backup?.date)} • {backup?.size}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-xs bg-accent text-accent-foreground px-2 py-1 rounded">
                  {backup?.type}
                </span>
                <Button variant="ghost" size="sm" onClick={() => handleRestoreBackup(backup?.id)}>
                  <Icon name="RotateCcw" size={16} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderConfigurationTab = () => (
    <div className="space-y-6">
      {/* Backup Schedule */}
      <div className="bg-card p-6 rounded-lg border border-border shadow-tier-1">
        <h3 className="text-lg font-semibold text-foreground mb-4">Agendamento de Backup</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Frequência</label>
            <Select
              value={backupSettings?.frequency}
              onChange={(value) => setBackupSettings({...backupSettings, frequency: value})}
              options={[
                { value: 'daily', label: 'Diário' },
                { value: 'weekly', label: 'Semanal' },
                { value: 'monthly', label: 'Mensal' },
                { value: 'manual', label: 'Manual' }
              ]}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Horário</label>
            <Input
              type="time"
              value={backupSettings?.time}
              onChange={(e) => setBackupSettings({...backupSettings, time: e?.target?.value})}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Retenção (dias)</label>
            <Input
              type="number"
              value={backupSettings?.retention}
              onChange={(e) => setBackupSettings({...backupSettings, retention: e?.target?.value})}
              placeholder="30"
            />
          </div>
        </div>
      </div>

      {/* Storage Configuration */}
      <div className="bg-card p-6 rounded-lg border border-border shadow-tier-1">
        <h3 className="text-lg font-semibold text-foreground mb-4">Configuração de Armazenamento</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Local de Armazenamento</label>
            <Select
              value={backupSettings?.storageLocation}
              onChange={(value) => setBackupSettings({...backupSettings, storageLocation: value})}
              options={[
                { value: 'local', label: 'Armazenamento Local' },
                { value: 'cloud', label: 'Nuvem (AWS S3)' },
                { value: 'both', label: 'Local + Nuvem' }
              ]}
            />
          </div>
          
          {backupSettings?.storageLocation !== 'local' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
              <Input
                placeholder="Access Key"
                value={backupSettings?.cloudCredentials?.accessKey}
                onChange={(e) => setBackupSettings({
                  ...backupSettings, 
                  cloudCredentials: {...backupSettings?.cloudCredentials, accessKey: e?.target?.value}
                })}
              />
              <Input
                type="password"
                placeholder="Secret Key"
                value={backupSettings?.cloudCredentials?.secretKey}
                onChange={(e) => setBackupSettings({
                  ...backupSettings, 
                  cloudCredentials: {...backupSettings?.cloudCredentials, secretKey: e?.target?.value}
                })}
              />
              <Input
                placeholder="Bucket Name"
                value={backupSettings?.cloudCredentials?.bucketName}
                onChange={(e) => setBackupSettings({
                  ...backupSettings, 
                  cloudCredentials: {...backupSettings?.cloudCredentials, bucketName: e?.target?.value}
                })}
              />
              <Select
                value={backupSettings?.cloudCredentials?.region}
                onChange={(value) => setBackupSettings({
                  ...backupSettings, 
                  cloudCredentials: {...backupSettings?.cloudCredentials, region: value}
                })}
                placeholder="Região"
                options={[
                  { value: 'us-east-1', label: 'US East (Virginia)' },
                  { value: 'us-west-2', label: 'US West (Oregon)' },
                  { value: 'eu-west-1', label: 'Europe (Ireland)' },
                  { value: 'sa-east-1', label: 'South America (São Paulo)' }
                ]}
              />
            </div>
          )}
        </div>
      </div>

      {/* Backup Scope */}
      <div className="bg-card p-6 rounded-lg border border-border shadow-tier-1">
        <h3 className="text-lg font-semibold text-foreground mb-4">Escopo do Backup</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <Checkbox
                checked={backupSettings?.includeUserData}
                onChange={(e) => setBackupSettings({...backupSettings, includeUserData: e?.target?.checked})}
              />
              <div>
                <label className="text-sm font-medium text-foreground">Dados de Usuários</label>
                <p className="text-xs text-muted-foreground">Perfis, progresso, certificados (~50 MB)</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <Checkbox
                checked={backupSettings?.includeCourseContent}
                onChange={(e) => setBackupSettings({...backupSettings, includeCourseContent: e?.target?.checked})}
              />
              <div>
                <label className="text-sm font-medium text-foreground">Conteúdo dos Cursos</label>
                <p className="text-xs text-muted-foreground">Módulos, lições, atividades (~200 MB)</p>
              </div>
            </div>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <Checkbox
                checked={backupSettings?.includeMedia}
                onChange={(e) => setBackupSettings({...backupSettings, includeMedia: e?.target?.checked})}
              />
              <div>
                <label className="text-sm font-medium text-foreground">Arquivos de Mídia</label>
                <p className="text-xs text-muted-foreground">Vídeos, imagens, documentos (~5 GB)</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <Checkbox
                checked={backupSettings?.includeSystemSettings}
                onChange={(e) => setBackupSettings({...backupSettings, includeSystemSettings: e?.target?.checked})}
              />
              <div>
                <label className="text-sm font-medium text-foreground">Configurações do Sistema</label>
                <p className="text-xs text-muted-foreground">Temas, integrações, configurações (~5 MB)</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Advanced Options */}
      <div className="bg-card p-6 rounded-lg border border-border shadow-tier-1">
        <h3 className="text-lg font-semibold text-foreground mb-4">Opções Avançadas</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <Checkbox
                checked={backupSettings?.encryptionEnabled}
                onChange={(e) => setBackupSettings({...backupSettings, encryptionEnabled: e?.target?.checked})}
              />
              <label className="text-sm font-medium text-foreground">Criptografia AES-256</label>
            </div>
            
            <div className="flex items-center space-x-3">
              <Checkbox
                checked={backupSettings?.compressionEnabled}
                onChange={(e) => setBackupSettings({...backupSettings, compressionEnabled: e?.target?.checked})}
              />
              <label className="text-sm font-medium text-foreground">Compressão de Arquivos</label>
            </div>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <Checkbox
                checked={backupSettings?.notifications}
                onChange={(e) => setBackupSettings({...backupSettings, notifications: e?.target?.checked})}
              />
              <label className="text-sm font-medium text-foreground">Notificações por E-mail</label>
            </div>
          </div>
        </div>
      </div>

      {/* Save Configuration */}
      <div className="flex justify-end space-x-3">
        <Button variant="outline">
          Restaurar Padrões
        </Button>
        <Button variant="default" data-testid="btn-save-config">
          Salvar Configurações
        </Button>
      </div>
    </div>
  );

  const renderHistoryTab = () => (
    <div className="bg-card rounded-lg border border-border shadow-tier-1">
      <div className="p-6 border-b border-border">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-foreground">Histórico de Backups</h3>
          <div className="flex space-x-2">
            <Button variant="outline" size="sm">
              <Icon name="Filter" size={16} className="mr-2" />
              Filtrar
            </Button>
            <Button variant="outline" size="sm">
              <Icon name="Download" size={16} className="mr-2" />
              Exportar
            </Button>
          </div>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-4 text-sm font-medium text-foreground">Nome</th>
              <th className="text-left p-4 text-sm font-medium text-foreground">Tipo</th>
              <th className="text-left p-4 text-sm font-medium text-foreground">Data/Hora</th>
              <th className="text-left p-4 text-sm font-medium text-foreground">Tamanho</th>
              <th className="text-left p-4 text-sm font-medium text-foreground">Status</th>
              <th className="text-left p-4 text-sm font-medium text-foreground">Integridade</th>
              <th className="text-left p-4 text-sm font-medium text-foreground">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {backups?.map((backup) => (
              <tr key={backup?.id} className="hover:bg-accent/5 transition-micro">
                <td className="p-4 text-sm text-foreground">{backup?.name}</td>
                <td className="p-4">
                  <span className="text-xs bg-accent text-accent-foreground px-2 py-1 rounded">
                    {backup?.type}
                  </span>
                </td>
                <td className="p-4 text-sm text-foreground">{formatDate(backup?.date)}</td>
                <td className="p-4 text-sm text-foreground">{backup?.size}</td>
                <td className="p-4">
                  <div className="flex items-center space-x-2">
                    <Icon name={getStatusIcon(backup?.status)} size={14} className={getStatusColor(backup?.status)} />
                    <span className={`text-sm ${getStatusColor(backup?.status)}`}>
                      {backup?.status === 'completed' ? 'Concluído' : backup?.status}
                    </span>
                  </div>
                </td>
                <td className="p-4">
                  <span className="text-sm text-success">✓ Verificado</span>
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-2">
                    <Button variant="ghost" size="sm" onClick={() => handleRestoreBackup(backup?.id)}>
                      <Icon name="RotateCcw" size={14} />
                    </Button>
                    <Button variant="ghost" size="sm">
                      <Icon name="Download" size={14} />
                    </Button>
                    <Button variant="ghost" size="sm">
                      <Icon name="Trash2" size={14} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderRestoreTab = () => (
    <div className="space-y-6">
      {/* Warning Alert */}
      <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="AlertTriangle" size={20} className="text-warning mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-warning mb-1">Atenção: Processo de Restauração</h4>
            <p className="text-sm text-warning/80">
              A restauração de backup substituirá dados atuais. Faça um backup de segurança antes de continuar.
              Durante o processo, o sistema ficará temporariamente indisponível.
            </p>
          </div>
        </div>
      </div>

      {/* Restore Options */}
      <div className="bg-card p-6 rounded-lg border border-border shadow-tier-1">
        <h3 className="text-lg font-semibold text-foreground mb-4">Selecionar Backup para Restauração</h3>
        <div className="space-y-3">
          {backups?.filter(b => b?.status === 'completed')?.map((backup) => (
            <div key={backup?.id} className="p-4 border border-border rounded-lg hover:bg-accent/5 transition-micro">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Icon name="Database" size={20} className="text-primary" />
                  <div>
                    <h4 className="font-medium text-foreground">{backup?.name}</h4>
                    <p className="text-sm text-muted-foreground">
                      {formatDate(backup?.date)} • {backup?.size} • 
                      Inclui: {backup?.includes?.join(', ')}
                    </p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleRestoreBackup(backup?.id)}
                  data-testid={`btn-restore-${backup?.id}`}
                >
                  <Icon name="RotateCcw" size={16} className="mr-2" />
                  Restaurar
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Selective Restore */}
      <div className="bg-card p-6 rounded-lg border border-border shadow-tier-1">
        <h3 className="text-lg font-semibold text-foreground mb-4">Restauração Seletiva</h3>
        <p className="text-muted-foreground mb-4">
          Escolha componentes específicos para restaurar de um backup completo
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <Checkbox />
              <label className="text-sm font-medium text-foreground">Dados de Usuários</label>
            </div>
            <div className="flex items-center space-x-3">
              <Checkbox />
              <label className="text-sm font-medium text-foreground">Conteúdo dos Cursos</label>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <Checkbox />
              <label className="text-sm font-medium text-foreground">Arquivos de Mídia</label>
            </div>
            <div className="flex items-center space-x-3">
              <Checkbox />
              <label className="text-sm font-medium text-foreground">Configurações do Sistema</label>
            </div>
          </div>
        </div>
        
        <div className="flex justify-end">
          <Button variant="secondary" disabled>
            <Icon name="RotateCcw" size={16} className="mr-2" />
            Restaurar Seleção
            <span className="ml-2 text-xs">(Em desenvolvimento)</span>
          </Button>
        </div>
      </div>
    </div>
  );

  const renderMonitoringTab = () => (
    <div className="space-y-6">
      {/* Live Activity */}
      <div className="bg-card p-6 rounded-lg border border-border shadow-tier-1">
        <h3 className="text-lg font-semibold text-foreground mb-4">Atividade em Tempo Real</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 bg-success rounded-full animate-pulse" />
              <span className="text-sm text-foreground">Sistema de backup operacional</span>
            </div>
            <span className="text-xs text-muted-foreground">Agora</span>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center space-x-3">
              <Icon name="Clock" size={16} className="text-primary" />
              <span className="text-sm text-foreground">Próximo backup agendado</span>
            </div>
            <span className="text-xs text-muted-foreground">Hoje às 02:00</span>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center space-x-3">
              <Icon name="CheckCircle" size={16} className="text-success" />
              <span className="text-sm text-foreground">Verificação de integridade concluída</span>
            </div>
            <span className="text-xs text-muted-foreground">5 min atrás</span>
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-card p-6 rounded-lg border border-border shadow-tier-1">
          <h4 className="text-sm font-medium text-muted-foreground mb-4">Tempo Médio de Backup</h4>
          <div className="flex items-end space-x-2 mb-4">
            <span className="text-2xl font-bold text-foreground">12.5</span>
            <span className="text-muted-foreground">minutos</span>
          </div>
          <div className="text-xs text-success">↓ 15% mais rápido que o mês passado</div>
        </div>

        <div className="bg-card p-6 rounded-lg border border-border shadow-tier-1">
          <h4 className="text-sm font-medium text-muted-foreground mb-4">Taxa de Sucesso</h4>
          <div className="flex items-end space-x-2 mb-4">
            <span className="text-2xl font-bold text-foreground">99.2</span>
            <span className="text-muted-foreground">%</span>
          </div>
          <div className="text-xs text-success">✓ Dentro do objetivo (&gt;99%)</div>
        </div>
      </div>

      {/* Alerts and Notifications */}
      <div className="bg-card p-6 rounded-lg border border-border shadow-tier-1">
        <h3 className="text-lg font-semibold text-foreground mb-4">Alertas e Notificações</h3>
        <div className="space-y-3">
          <div className="p-3 bg-success/10 border border-success/20 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="CheckCircle" size={16} className="text-success" />
              <span className="text-sm font-medium text-success">Sistema operando normalmente</span>
            </div>
            <p className="text-xs text-success/80 mt-1">
              Todos os backups foram executados com sucesso nas últimas 24 horas
            </p>
          </div>
          
          <div className="p-3 bg-warning/10 border border-warning/20 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Icon name="AlertTriangle" size={16} className="text-warning" />
                <span className="text-sm font-medium text-warning">Espaço em disco baixo</span>
              </div>
              <Button variant="outline" size="sm">
                Gerenciar Espaço
              </Button>
            </div>
            <p className="text-xs text-warning/80 mt-1">
              Considere limpar backups antigos ou expandir o armazenamento
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverviewTab();
      case 'configuration':
        return renderConfigurationTab();
      case 'history':
        return renderHistoryTab();
      case 'restore':
        return renderRestoreTab();
      case 'monitoring':
        return renderMonitoringTab();
      default:
        return renderOverviewTab();
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <AdminSidebar 
        isCollapsed={sidebarCollapsed} 
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <main className={`transition-all duration-300 ${sidebarCollapsed ? 'ml-16' : 'ml-72'} pt-16`}>
        <div className="p-6">
          <Breadcrumb customItems={breadcrumbItems} />
          
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                Configuração de Backup do Sistema
              </h1>
              <p className="text-muted-foreground font-caption">
                Gerencie backups automáticos, configurações de segurança e restauração de dados
              </p>
            </div>
            
            <div className="flex items-center space-x-3 mt-4 lg:mt-0">
              <Button variant="outline" iconName="TestTube" iconPosition="left" iconSize={16}>
                Testar Backup
              </Button>
              <Button 
                variant="default" 
                iconName="Database" 
                iconPosition="left" 
                iconSize={16}
                data-testid="btn-emergency-backup"
                onClick={handleBackupNow}
                disabled={backupProgress?.isRunning}
              >
                {backupProgress?.isRunning ? 'Executando...' : 'Backup de Emergência'}
              </Button>
            </div>
          </div>

          {/* Tabs Navigation */}
          <div className="bg-card border border-border rounded-lg mb-6">
            <div className="border-b border-border">
              <nav className="flex space-x-8 px-6 overflow-x-auto">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => handleTabChange(tab?.id)}
                    className={`flex items-center space-x-2 py-4 px-2 border-b-2 font-medium text-sm transition-micro whitespace-nowrap ${
                      activeTab === tab?.id
                        ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                    }`}
                  >
                    <Icon name={tab?.icon} size={16} />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {renderTabContent()}
            </div>
          </div>
        </div>
      </main>
      <FloatingAIChat />
    </div>
  );
};

export default SystemBackupConfiguration;